import { storeToRefs } from 'pinia'
import { createRouter, createWebHistory } from 'vue-router'

import { ViewName } from '@/types'
import { useAuthStore } from '@/store/AuthStore'
import { useLoginStore } from '@/store/LoginStore'
import {
  canResetPassword,
  fetchFooterData,
  handleHostRedirect,
  handlePasswordResetCode,
  handleRefresh,
  handleRemote,
  redirectToRefresh,
  removeReferrer,
  setAppReferrer,
  setChallengeId,
  setFeatureFlags,
  setProduct,
  setViewName
} from './navigationGuards'

const router = createRouter({
  history: createWebHistory('/'),
  routes: [
    {
      path: '/',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      props: route => {
        return {
          isLogin: true,
          title: 'Log in to your account',
          description: 'Enter the email address associated with<br>your Output account.',
          query: route.query,
          params: route.params
        }
      },
      meta: {
        viewContextName: ViewName.Login
      },
      beforeEnter: [setFeatureFlags, fetchFooterData, setViewName]
    },
    {
      path: '/signin/:productName?',
      name: 'signIn',
      component: () => import('@/views/LoginView.vue'),
      beforeEnter: [setFeatureFlags, fetchFooterData, setProduct, setViewName, setChallengeId, setAppReferrer, redirectToRefresh],
      meta: {
        viewContextName: ViewName.Login
      },
      props: route => {
        return {
          isLogin: true,
          title: 'Log in to Output',
          description: 'Enter the email address associated with<br>your Output account.',
          query: route.query,
          params: route.params
        }
      }
    },
    {
      path: '/refresh/:productName?',
      name: 'refresh',
      component: () => import('@/views/LoginView.vue'),
      props: route => ({
        isLoading: true,
        query: route.query,
        params: route.params
      }),
      beforeEnter: [fetchFooterData, handleRefresh, setProduct]
    },
    {
      path: '/create/:productName?',
      name: 'create',
      component: () => import('@/views/LoginView.vue'),
      meta: { viewContextName: ViewName.Create },
      props: route => {
        return {
          isCreateNewAccount: true,
          query: route.query,
          params: route.params,
          title: 'Create an Account',
          description: 'Enter your information then click<br>‘Create Account’ to get started.'
        }
      },
      beforeEnter: [setFeatureFlags, fetchFooterData, setProduct, setViewName]
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/views/ResetPasswordView.vue'),
      meta: {
        viewContextName: ViewName.Login
      },
      props: () => {
        return {
          isResetPassword: true
        }
      },
      beforeEnter: [setFeatureFlags, handlePasswordResetCode, canResetPassword, fetchFooterData, setViewName]
    },
    {
      path: '/success/:productName?',
      name: 'success',
      component: () => import('@/views/SuccessView.vue'),
      props: () => {
        return {
          isSuccess: true
        }
      },
      meta: {
        viewContextName: ViewName.Success,
        requiresAuth: true
      },
      beforeEnter: [setFeatureFlags, fetchFooterData, handleRemote, handleHostRedirect, removeReferrer, setAppReferrer, setProduct, setViewName]
    },
    {
      path: '/logout/:productName?',
      name: 'logout',
      redirect: to => {
        const { query, params } = to
        const { handleLogout } = useLoginStore()

        handleLogout()

        const destination = {
          name: 'signIn',
          query,
          params
        }
        return destination
      }
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/ForgotPasswordView.vue'),
      meta: {
        viewContextName: ViewName.Login
      },
      beforeEnter: [fetchFooterData, setViewName]
    },
    {
      path: '/reset-password-success',
      name: 'reset-password-success',
      component: () => import('@/views/ResetPasswordSuccessView.vue'),
      meta: {
        viewContextName: ViewName.Login
      },
      beforeEnter: [fetchFooterData, setViewName]
    },
    {
      path: '/oauth/callback',
      name: 'oauth-callback',
      component: () => import('@/views/OAuthCallbackView.vue')
    }
  ]
})

router.beforeEach(to => {
  const { params, meta, name, query } = to
  const authStore = useAuthStore()
  const { setTokens } = authStore
  const { isUserAuthenticated } = storeToRefs(authStore)

  setTokens()

  if (meta.requiresAuth && !isUserAuthenticated.value) {
    const referrer = name as string
    query.referrer = referrer

    // redirect the user to the login page
    return {
      name: 'signIn',
      query,
      params
    }
  }
})

export default router
